// Merchant Sales Report (MM-3037). Admin / Super Admin pick a Master Distributor or Distributor and a reporting
// period (up to 30 days) and see recharge sales per MNO and stock sales for that merchant and its whole
// hierarchy, as of the end of the previous day.
//
// Data comes from the nightly snapshot (backend: services/Admin_Web/reports, reports/merchant-sales/*):
//   /summary   data_as_of + MNO columns on load; the selected merchant's row and the Grand Total once picked
//   /children  one page (20) of a merchant's direct children, on expand and "Load more"
//   /search    the merchant picker, 25 matches per page (infinite scroll)
//   /export    CSV / Excel of the selected merchant's whole hierarchy (MM-3168); over 3000 merchants it is
//              emailed as a link that expires after 6 hours
// Every row carries the merchant's own and subtree figures, and the Grand Total comes from the server, so this
// page never adds amounts together.
import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { CalendarDays, Info } from "lucide-react";
import useApi from "../../../services/useApi";
import endpoints from "../../../services/endpoints";
import Image from "../../../components/images/Image";
import EmptyListingScreen from "../../../components/emptyScreens/EmptyListingScreen";
import { showToast } from "../../../components/toastMessage/toastSlice";
import MerchantSalesTable, { MerchantSalesTableSkeleton } from "./MerchantSalesTable";
import MerchantSearch from "./MerchantSearch";
import ReportingPeriodFilter from "./ReportingPeriodFilter";
import ExportMenu, { ExportFormat } from "./ExportMenu";
import { CHILD_PAGE_SIZE, ReportController } from "./reportController";
import { defaultRange, formatDisplayDate, formatPeriod } from "./dateRange";
import { ChildrenPage, DateRange, Figures, MerchantOption, MerchantRow, Mno, SummaryData } from "./types";

const DEFAULT_ERROR = "Oops! Something went wrong. Please try again later.";
const SEARCH_PAGE_SIZE = 25;

interface ApiBody<T> {
  message?: string;
  data: T;
  next_cursor?: string | null;
  has_more?: boolean;
  total_count?: number;
}

const MerchantSalesReport = () => {
  const dispatch = useDispatch();
  const { getAPI } = useApi();
  // useApi returns a new function every render; keep the latest in a ref so callbacks stay stable.
  const getAPIRef = useRef(getAPI);
  getAPIRef.current = getAPI;

  const [meta, setMeta] = useState<{ dataAsOf: string | null; mnos: Mno[] } | null>(null);
  const [metaError, setMetaError] = useState<string | null>(null);
  const [range, setRange] = useState<DateRange | null>(null);
  const [selected, setSelected] = useState<MerchantOption | null>(null);
  const [noResultsQuery, setNoResultsQuery] = useState<string | null>(null);
  const [searchResetKey, setSearchResetKey] = useState(0);
  const [report, setReport] = useState<{ controller: ReportController; grandTotal: Figures; mnos: Mno[] } | null>(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [version, bump] = useReducer((x: number) => x + 1, 0);

  const callApi = useCallback(async <T,>(path: string): Promise<ApiBody<T>> => {
    const res = await getAPIRef.current(`${endpoints.merchantSalesReport}${path}`);
    if (res.error || !res.data) throw new Error(res.data?.data?.message || DEFAULT_ERROR);
    return res.data as ApiBody<T>;
  }, []);

  const toastError = useCallback(
    (message: string) => dispatch(showToast({ type: "error", message })),
    [dispatch],
  );

  // Initial load: "Data as of", the default period and the MNO columns. Nothing else loads until a merchant is picked.
  useEffect(() => {
    let cancelled = false;
    callApi<SummaryData>("/summary")
      .then((body) => {
        if (cancelled) return;
        setMeta({ dataAsOf: body.data.data_as_of, mnos: body.data.mnos });
        if (body.data.data_as_of) setRange(defaultRange(body.data.data_as_of));
      })
      .catch((error: Error) => {
        if (!cancelled) setMetaError(error.message);
      });
    return () => {
      cancelled = true;
    };
  }, [callApi]);

  // A merchant or period change rebuilds the report from scratch: new controller, empty cache.
  useEffect(() => {
    if (!selected || !range) {
      setReport(null);
      return;
    }
    let cancelled = false;
    let controller: ReportController | null = null;
    const dates = `start_date=${range.start}&end_date=${range.end}`;
    setReportLoading(true);
    setReportError(null);
    setReport(null);

    callApi<SummaryData>(`/summary?merchant_id=${encodeURIComponent(selected.id)}&${dates}`)
      .then((body) => {
        if (cancelled) return;
        const { merchant, grand_total, mnos } = body.data;
        if (!merchant || !grand_total) throw new Error(DEFAULT_ERROR);
        const fetchChildren = async (parentId: string, cursor: string | null): Promise<ChildrenPage> => {
          const page = await callApi<MerchantRow[]>(
            `/children?parent_id=${encodeURIComponent(parentId)}&${dates}&limit=${CHILD_PAGE_SIZE}` +
              (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""),
          );
          return {
            items: page.data,
            next_cursor: page.next_cursor ?? null,
            has_more: !!page.has_more,
            total_count: page.total_count ?? page.data.length,
          };
        };
        controller = new ReportController(merchant, fetchChildren, { onChange: bump, onError: toastError });
        setReport({ controller, grandTotal: grand_total, mnos });
      })
      .catch((error: Error) => {
        if (cancelled) return;
        setReportError(error.message);
        toastError(error.message);
      })
      .finally(() => {
        if (!cancelled) setReportLoading(false);
      });

    return () => {
      cancelled = true;
      controller?.dispose();
    };
  }, [selected, range, callApi, toastError]);

  const rows = useMemo(
    () => report?.controller.store.flattenVisible() ?? [],
    // `version` changes whenever the controller's store does.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [report, version],
  );

  const onToggle = useCallback((id: string) => report?.controller.toggle(id), [report]);
  const onLoadMore = useCallback((parentId: string) => report?.controller.loadMore(parentId), [report]);

  const validateExport = () => {
    if (!selected) return "Please select a merchant to export";
    if (!range) return "Please select a reporting period to export";
    return null;
  };

  const exportReport = async (format: ExportFormat) => {
    if (!selected || !range) return;
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const res = await getAPIRef.current(
      `${endpoints.merchantSalesReport}/export?merchant_id=${encodeURIComponent(selected.id)}` +
        `&start_date=${range.start}&end_date=${range.end}&export_format=${format}&client_timezone=${encodeURIComponent(timezone)}`,
    );
    if (res.error || !res.data) {
      toastError(res.data?.data?.message || DEFAULT_ERROR);
      return;
    }
    if (res.data.url) {
      window.open(res.data.url, "_blank");
      dispatch(showToast({ type: "success", message: "Merchant Sales Report exported successfully" }));
      return;
    }
    // Over 3000 merchants: the file is emailed, and the message says the link expires after 6 hours.
    dispatch(
      showToast({
        type: "success",
        message:
          res.data.message ||
          `Export in progress. You'll receive an email with the ${format.toUpperCase()} once it's ready. The link will expire after 6 hours.`,
      }),
    );
  };

  const searchMerchants = useCallback(
    async (q: string, page: number) => {
      const body = await callApi<MerchantOption[]>(
        `/search?q=${encodeURIComponent(q)}&limit=${SEARCH_PAGE_SIZE}&page_number=${page}`,
      );
      return { items: body.data, hasMore: !!body.has_more };
    },
    [callApi],
  );

  const clearSearchAndFilters = () => {
    setSelected(null);
    setNoResultsQuery(null);
    setSearchResetKey((k) => k + 1);
    if (meta?.dataAsOf) setRange(defaultRange(meta.dataAsOf));
  };

  const dataAsOf = meta?.dataAsOf ?? null;
  const selectedLabel = selected ? `${selected.name} (${selected.user_code})` : "";

  const renderBody = () => {
    if (metaError) {
      return <EmptyListingScreen emptyScreenText={{ heading1: "Unable to load the report", heading2: metaError }} />;
    }
    if (!meta) return <MerchantSalesTableSkeleton />;
    if (!dataAsOf) {
      return (
        <EmptyListingScreen
          emptyScreenText={{
            heading1: "Report data is not available yet",
            heading2: "The Merchant Sales Report is prepared overnight. Please check back tomorrow.",
          }}
        />
      );
    }
    if (noResultsQuery) {
      return (
        // Figma 3071:19696 "empty-state-content", centred in the card.
        <div className="flex flex-1 items-center justify-center" data-testid="merchant-sales-no-results">
          <div className="flex w-[420px] max-w-full flex-col items-center gap-3">
            <Image src="empty-search" className="size-60" />
            <div className="flex flex-col items-center gap-1.5 text-center">
              <p className="text-lg font-semibold leading-7 text-primary_text">No merchants found</p>
              <p className="text-[13px] leading-[1.4] text-[#6b7385]">
                {`No results for “${noResultsQuery}”. Try another name or code.`}
              </p>
            </div>
            <button
              type="button"
              className="rounded-md border border-p_primary px-4 py-2 text-sm font-semibold leading-6 text-p_primary"
              onClick={clearSearchAndFilters}
            >
              Clear search & filters
            </button>
          </div>
        </div>
      );
    }
    if (!selected) {
      return (
        <EmptyListingScreen
          emptyScreenText={{
            heading1: "Select a merchant to view the report",
            heading2: "Search a merchant by name or merchant code",
          }}
        />
      );
    }
    if (reportLoading) return <MerchantSalesTableSkeleton />;
    if (reportError || !report) {
      return <EmptyListingScreen emptyScreenText={{ heading1: "Unable to load the report", heading2: reportError ?? DEFAULT_ERROR }} />;
    }
    return (
      <MerchantSalesTable
        rows={rows}
        store={report.controller.store}
        mnos={report.mnos}
        grandTotal={report.grandTotal}
        resetKey={`${selected.id}:${range?.start}:${range?.end}`}
        onToggle={onToggle}
        onLoadMore={onLoadMore}
      />
    );
  };

  return (
    // Same outer height as GridTable pages (98px top bar + h-maintableheight card), so the card reaches the bottom.
    <div className="flex h-[calc(100vh-125px)] flex-col gap-4 font-[Montserrat]">
      {/* Figma header card: title, reporting period, data as of. */}
      <div className="flex flex-wrap items-center gap-4 rounded-lg bg-[#fefefe] px-6 py-4">
        <h1 className="flex-1 text-2xl font-semibold leading-8 text-primary_text">Merchant Sales Report</h1>
        {range && (
          <div className="flex items-start gap-2">
            <CalendarDays size={24} className="text-p_primary" />
            <div className="flex flex-col gap-1">
              <span className="text-xs leading-5 text-[#5c5c5c]">Reporting Period</span>
              <span className="text-xs font-semibold uppercase text-[#1a1a1a]" data-testid="merchant-sales-period">
                {formatPeriod(range)}
              </span>
            </div>
          </div>
        )}
        {range && <div className="w-px self-stretch bg-outline" />}
        <div className="flex items-start gap-2">
          <Info size={24} className="text-p_primary" />
          <div className="flex flex-col gap-1">
            <span className="text-xs leading-5 text-[#5c5c5c]">Data as of</span>
            <span className="text-xs font-semibold uppercase text-[#1a1a1a]" data-testid="merchant-sales-data-as-of">
              {dataAsOf ? formatDisplayDate(dataAsOf) : meta || metaError ? "—" : <span className="block h-4 w-20 animate-pulse rounded bg-gray-200" />}
            </span>
          </div>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4 rounded-lg bg-white p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <MerchantSearch
            selectedLabel={selectedLabel}
            fetchOptions={searchMerchants}
            onSelect={(option) => {
              setNoResultsQuery(null);
              setSelected(option);
            }}
            onNoResults={setNoResultsQuery}
            onClear={() => setSelected(null)}
            resetKey={searchResetKey}
          />
          <div className="flex items-center gap-4">
            {dataAsOf && range && <ReportingPeriodFilter dataAsOf={dataAsOf} value={range} onApply={setRange} />}
            {dataAsOf && (
              <ExportMenu
                validate={validateExport}
                onInvalid={toastError}
                onExport={exportReport}
                disabled={reportLoading}
              />
            )}
          </div>
        </div>
        {renderBody()}
      </div>
    </div>
  );
};

export default MerchantSalesReport;
